import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    getChallan, createChallan, updateChallan, addChallanLine,
    updateChallanLine, deleteChallanLine, getChallanPdfUrl,
    updateInternalNotes, duplicateChallan
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { CustomerPicker } from '../components/CustomerPicker';
import {
    ArrowLeft, Plus, Trash, FloppyDisk, FilePdf, Eye, Printer,
    Package, PuzzlePiece, PencilSimpleLine, Copy, Buildings, Truck, UserCircle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const TYPES = ['Outward', 'Inward', 'Return', 'Job Work', 'Transfer', 'Sample'];
const DEST_TYPES = ['Fabricator', 'Customer', 'Warehouse', 'Vendor', 'Office', 'Transporter'];
const RETURN_TYPES = ['returnable', 'non-returnable'];
const STATUSES = [
    { value: 'draft', label: 'Draft' },
    { value: 'issued', label: 'Issued' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
];
const UNITS = ['pcs', 'nos', 'sheets', 'kg', 'm', 'sqm', 'sqft', 'box', 'set', 'roll', 'bundle'];

export default function ChallanBuilder() {
    const { challanId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isNew = !challanId || challanId === 'new';

    const [challan, setChallan] = useState(null);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [addLineOpen, setAddLineOpen] = useState(false);
    const [internalNotes, setInternalNotes] = useState('');
    const notesTimerRef = useRef(null);
    const [header, setHeader] = useState({
        challan_date: new Date().toISOString().slice(0, 10),
        challan_type: 'Outward',
        return_type: 'non-returnable',
        source_location: 'Crown / Lamikraft Office',
        destination_type: '',
        destination_party: '',
        recipient_type: 'other',
        customer_id: '',
        shipping_name: '',
        gstin: '',
        phone: '',
        email: '',
        address: '',
        reference_no: '',
        remarks: '',
    });
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const loadChallan = useCallback(async () => {
        if (isNew) return;
        try {
            const res = await getChallan(challanId);
            setChallan(res.data.challan);
            setLines(res.data.lines || []);
            const c = res.data.challan;
            setHeader({
                challan_date: c.challan_date || '',
                challan_type: c.challan_type || 'Outward',
                return_type: c.return_type || 'non-returnable',
                source_location: c.source_location || '',
                destination_type: c.destination_type || '',
                destination_party: c.destination_party || '',
                recipient_type: c.recipient_type || (c.customer_id ? 'customer' : 'other'),
                customer_id: c.customer_id || '',
                shipping_name: c.shipping_name || '',
                gstin: c.gstin || '',
                phone: c.phone || '',
                email: c.email || '',
                address: c.address || '',
                reference_no: c.reference_no || '',
                remarks: c.remarks || '',
            });
            if (c.customer_id) {
                setSelectedCustomer({ id: c.customer_id, company_name: c.destination_party, country: '' });
            }
            setInternalNotes(c.internal_notes || '');
        } catch { toast.error('Failed to load challan'); }
        finally { setLoading(false); }
    }, [challanId, isNew]);

    useEffect(() => { loadChallan(); }, [loadChallan]);

    const onHeaderChange = (e) => {
        const { name, value } = e.target;
        setHeader(prev => ({ ...prev, [name]: value }));
    };

    const handleNotesChange = (val) => {
        setInternalNotes(val);
        if (!isNew) {
            clearTimeout(notesTimerRef.current);
            notesTimerRef.current = setTimeout(() => {
                updateInternalNotes('challan', challanId, val).catch(() => {});
            }, 1000);
        }
    };

    const handleRecipientTypeChange = (type) => {
        setHeader(prev => ({ ...prev, recipient_type: type, customer_id: '' }));
        setSelectedCustomer(null);
        if (type !== 'customer') {
            // Keep existing text fields as-is when switching to vendor/other
        }
    };

    const handleChallanCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        const pc = (customer.contacts || []).find(c => c.is_primary) || (customer.contacts || [])[0];
        const defaultShipping = (customer.shipping_addresses || []).find(a => a.is_default) || (customer.shipping_addresses || [])[0];
        const billingAddr = customer.billing_address || {};
        const addr = defaultShipping
            ? [defaultShipping.line1, defaultShipping.line2, defaultShipping.city, defaultShipping.state, defaultShipping.postal_code, defaultShipping.country].filter(Boolean).join(', ')
            : [billingAddr.line1, billingAddr.line2, billingAddr.city, billingAddr.state, billingAddr.postal_code, billingAddr.country].filter(Boolean).join(', ');
        setHeader(prev => ({
            ...prev,
            customer_id: customer.id,
            destination_party: customer.company_name || '',
            destination_type: 'Customer',
            shipping_name: pc?.name || prev.shipping_name,
            email: pc?.email || prev.email,
            phone: pc?.phone || prev.phone,
            gstin: billingAddr.tax_id || prev.gstin,
            address: addr || prev.address,
        }));
    };

    const handleChallanCustomerClear = () => {
        setSelectedCustomer(null);
        setHeader(prev => ({ ...prev, customer_id: '' }));
    };

    const handleDuplicate = async () => {
        try {
            const res = await duplicateChallan(challanId);
            toast.success(`Duplicated as ${res.data.challan_number}`);
            navigate(`/challans/${res.data.id}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to duplicate'); }
    };

    const handleSaveHeader = async () => {
        if (!header.destination_party) {
            toast.error('Destination party name is required');
            return;
        }
        setSaving(true);
        try {
            if (isNew) {
                const res = await createChallan(header);
                toast.success(`Challan ${res.data.challan_number} created`);
                navigate(`/challans/${res.data.id}`, { replace: true });
            } else {
                await updateChallan(challanId, header);
                toast.success('Challan updated');
                loadChallan();
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await updateChallan(challanId, { status: newStatus });
            toast.success(`Status: ${newStatus}`);
            loadChallan();
        } catch (err) { toast.error(err.response?.data?.detail || 'Status update failed'); }
    };

    const handleAddLine = async (lineData) => {
        try {
            await addChallanLine(challanId, lineData);
            toast.success('Line added');
            setAddLineOpen(false);
            loadChallan();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add line'); }
    };

    const handleDeleteLine = async (lineId) => {
        try {
            await deleteChallanLine(challanId, lineId);
            setLines(prev => prev.filter(l => l.id !== lineId));
            toast.success('Line removed');
        } catch { toast.error('Failed to remove line'); }
    };

    const handleQtyChange = async (lineId, newQty) => {
        const qty = parseFloat(newQty);
        if (isNaN(qty) || qty < 0) return;
        setLines(prev => prev.map(l => l.id === lineId ? { ...l, quantity: qty } : l));
        try {
            await updateChallanLine(challanId, lineId, { quantity: qty });
        } catch { /* silent */ }
    };

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;

    return (
        <div className="space-y-6" data-testid="challan-builder">
            {/* Top bar */}
            <div className="flex items-center justify-between">
                <div>
                    <Button variant="ghost" size="sm" asChild className="mb-1 -ml-2">
                        <Link to="/challans"><ArrowLeft size={16} className="mr-1" />Challans</Link>
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight" data-testid="challan-title">
                        {isNew ? 'New Delivery Challan' : challan?.challan_number || 'Challan'}
                    </h1>
                    {challan && <p className="text-sm text-muted-foreground">{challan.challan_type} — {challan.status}</p>}
                </div>
                <div className="flex gap-2">
                    {!isNew && (
                        <>
                            <Button variant="outline" size="sm" asChild>
                                <Link to={`/challans/${challanId}/preview`}><Eye size={16} className="mr-2" />Preview</Link>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(getChallanPdfUrl(challanId), '_blank')} data-testid="export-pdf-btn">
                                <FilePdf size={16} className="mr-2" />PDF
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-challan-btn">
                                <Copy size={16} className="mr-2" />Duplicate
                            </Button>
                            <Select value={challan?.status || 'draft'} onValueChange={handleStatusChange}>
                                <SelectTrigger className="w-[140px] h-9" data-testid="status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </>
                    )}
                </div>
            </div>

            {/* Header Form */}
            <div className="bg-card border border-border rounded-lg">
                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Challan Header</h3>
                    <Button size="sm" onClick={handleSaveHeader} disabled={saving} data-testid="save-header-btn">
                        <FloppyDisk size={16} className="mr-2" />{saving ? 'Saving...' : isNew ? 'Create Challan' : 'Save'}
                    </Button>
                </div>
                <div className="p-5 space-y-4">
                    {/* Row 1: Date, Type, Returnable */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Challan Date</Label>
                            <Input type="date" name="challan_date" value={header.challan_date} onChange={onHeaderChange} data-testid="field-date" />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Challan Type</Label>
                            <Select value={header.challan_type} onValueChange={v => setHeader(p => ({ ...p, challan_type: v }))}>
                                <SelectTrigger data-testid="field-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Returnable?</Label>
                            <Select value={header.return_type} onValueChange={v => setHeader(p => ({ ...p, return_type: v }))}>
                                <SelectTrigger data-testid="field-return"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="returnable">Returnable</SelectItem>
                                    <SelectItem value="non-returnable">Non-Returnable</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Row 2: Source + Destination Type */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Source Location</Label>
                            <Input name="source_location" value={header.source_location} onChange={onHeaderChange} data-testid="field-source" />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Destination Type</Label>
                            <Select value={header.destination_type} onValueChange={v => setHeader(p => ({ ...p, destination_type: v }))}>
                                <SelectTrigger data-testid="field-dest-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                                <SelectContent>{DEST_TYPES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Reference No.</Label>
                            <Input name="reference_no" value={header.reference_no} onChange={onHeaderChange} data-testid="field-ref" />
                        </div>
                    </div>

                    {/* Recipient Type Toggle + Party Section */}
                    <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/20">
                        <div>
                            <Label className="text-xs text-muted-foreground mb-2 block">Recipient</Label>
                            <div className="flex gap-1.5" data-testid="recipient-type-toggle">
                                {[
                                    { value: 'customer', label: 'Customer', icon: Buildings },
                                    { value: 'vendor', label: 'Vendor', icon: Truck },
                                    { value: 'other', label: 'Other', icon: UserCircle },
                                ].map(t => (
                                    <Button
                                        key={t.value}
                                        variant={header.recipient_type === t.value ? 'default' : 'outline'}
                                        size="sm"
                                        className="h-8 text-xs gap-1.5"
                                        onClick={() => handleRecipientTypeChange(t.value)}
                                        data-testid={`recipient-${t.value}`}
                                    >
                                        <t.icon size={14} />{t.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {header.recipient_type === 'customer' ? (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Customer</Label>
                                    <CustomerPicker
                                        value={selectedCustomer}
                                        onSelect={handleChallanCustomerSelect}
                                        onClear={handleChallanCustomerClear}
                                        placeholder="Search customer by name, contact or email..."
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Party Name {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="destination_party" value={header.destination_party} onChange={onHeaderChange} placeholder="Party / Company name" data-testid="field-party" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Shipping Name {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="shipping_name" value={header.shipping_name} onChange={onHeaderChange} data-testid="field-shipping" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">GSTIN {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="gstin" value={header.gstin} onChange={onHeaderChange} placeholder="e.g. 27ABCDE1234F1Z5" data-testid="field-gstin" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Phone {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="phone" value={header.phone} onChange={onHeaderChange} data-testid="field-phone" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Email {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="email" value={header.email} onChange={onHeaderChange} data-testid="field-email" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground mb-1 block">Address {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                                        <Input name="address" value={header.address} onChange={onHeaderChange} data-testid="field-address" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">
                                        {header.recipient_type === 'vendor' ? 'Vendor Name' : 'Party Name'}
                                    </Label>
                                    <Input name="destination_party" value={header.destination_party} onChange={onHeaderChange} placeholder="Party / Company name" data-testid="field-party" />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Shipping Name</Label>
                                    <Input name="shipping_name" value={header.shipping_name} onChange={onHeaderChange} data-testid="field-shipping" />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">GSTIN</Label>
                                    <Input name="gstin" value={header.gstin} onChange={onHeaderChange} placeholder="e.g. 27ABCDE1234F1Z5" data-testid="field-gstin" />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Phone</Label>
                                    <Input name="phone" value={header.phone} onChange={onHeaderChange} data-testid="field-phone" />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Email</Label>
                                    <Input name="email" value={header.email} onChange={onHeaderChange} data-testid="field-email" />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1 block">Address</Label>
                                    <Input name="address" value={header.address} onChange={onHeaderChange} data-testid="field-address" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Remarks */}
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Remarks</Label>
                        <Textarea name="remarks" value={header.remarks} onChange={onHeaderChange} rows={2} data-testid="field-remarks" />
                    </div>
                </div>
            </div>

            {/* Line Items */}
            {!isNew && (
                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                        <h3 className="font-semibold text-sm">
                            Line Items <Badge variant="secondary" className="ml-2 text-xs">{lines.length}</Badge>
                        </h3>
                        <Button variant="outline" size="sm" onClick={() => setAddLineOpen(true)} data-testid="add-line-btn">
                            <Plus size={14} className="mr-1" />Add Item
                        </Button>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10">#</TableHead>
                                <TableHead>Code</TableHead>
                                <TableHead>Item Name</TableHead>
                                <TableHead>HSN/SAC</TableHead>
                                <TableHead className="w-24">Qty</TableHead>
                                <TableHead>Unit</TableHead>
                                <TableHead>Remarks</TableHead>
                                <TableHead className="w-10"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {lines.length === 0 ? (
                                <TableRow><TableCell colSpan={8} className="h-16 text-center text-muted-foreground text-sm">
                                    No items yet. Add materials to this challan.
                                </TableCell></TableRow>
                            ) : lines.map((line, idx) => (
                                <TableRow key={line.id} data-testid={`line-row-${idx}`}>
                                    <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                                    <TableCell className="font-mono text-xs">{line.item_code || '—'}</TableCell>
                                    <TableCell className="text-sm">{line.item_name || '—'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{line.hsn_code || '—'}</TableCell>
                                    <TableCell>
                                        <Input
                                            type="number" min={0} step="any"
                                            value={line.quantity}
                                            onChange={e => handleQtyChange(line.id, e.target.value)}
                                            className="h-7 w-20 text-xs font-mono"
                                            data-testid={`qty-${line.id}`}
                                        />
                                    </TableCell>
                                    <TableCell className="text-xs">{line.unit}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{line.remarks || '—'}</TableCell>
                                    <TableCell>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteLine(line.id)} data-testid={`delete-line-${idx}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {lines.length > 0 && (
                        <div className="px-5 py-2 border-t border-border text-right text-sm">
                            <span className="text-muted-foreground mr-2">Total Qty:</span>
                            <span className="font-semibold font-mono">{lines.reduce((s, l) => s + (l.quantity || 0), 0)}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Internal Notes */}
            {!isNew && (
                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3 border-b border-border">
                        <h3 className="font-semibold text-sm">Internal Notes</h3>
                    </div>
                    <div className="p-4">
                        <textarea
                            value={internalNotes}
                            onChange={e => handleNotesChange(e.target.value)}
                            placeholder="Add internal notes about this challan (auto-saves)..."
                            className="w-full min-h-[60px] text-sm bg-muted/30 border border-border rounded-md p-3 resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid="internal-notes"
                        />
                    </div>
                </div>
            )}

            {/* Add Line Dialog */}
            <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Line Item</DialogTitle>
                        <DialogDescription>Add a material or product line to this challan.</DialogDescription>
                    </DialogHeader>
                    <AddLineForm onAdd={handleAddLine} onClose={() => setAddLineOpen(false)} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AddLineForm({ onAdd, onClose }) {
    const [form, setForm] = useState({
        line_type: 'manual',
        item_code: '',
        item_name: '',
        hsn_code: '',
        quantity: 1,
        unit: 'pcs',
        remarks: '',
    });

    const handleSubmit = () => {
        if (!form.item_name) { toast.error('Item name is required'); return; }
        if (!form.quantity || form.quantity <= 0) { toast.error('Quantity must be > 0'); return; }
        onAdd(form);
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                {[
                    { value: 'catalog', label: 'Catalog', icon: Package },
                    { value: 'custom', label: 'Custom', icon: PuzzlePiece },
                    { value: 'manual', label: 'Manual', icon: PencilSimpleLine },
                ].map(t => (
                    <Button
                        key={t.value}
                        variant={form.line_type === t.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setForm(p => ({ ...p, line_type: t.value }))}
                        data-testid={`line-type-${t.value}`}
                    >
                        <t.icon size={14} className="mr-1" />{t.label}
                    </Button>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Item Code</Label>
                    <Input value={form.item_code} onChange={e => setForm(p => ({ ...p, item_code: e.target.value }))} placeholder="e.g. CB01-1" data-testid="line-item-code" />
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">HSN / SAC Code</Label>
                    <Input value={form.hsn_code} onChange={e => setForm(p => ({ ...p, hsn_code: e.target.value }))} placeholder="e.g. 4411" data-testid="line-hsn" />
                </div>
            </div>
            <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Item Name / Description</Label>
                <Input value={form.item_name} onChange={e => setForm(p => ({ ...p, item_name: e.target.value }))} placeholder="Chopping Board CB01 - 150x230mm - 4mm" data-testid="line-item-name" />
            </div>
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Quantity</Label>
                    <Input type="number" min={1} value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} data-testid="line-quantity" />
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Unit</Label>
                    <Select value={form.unit} onValueChange={v => setForm(p => ({ ...p, unit: v }))}>
                        <SelectTrigger data-testid="line-unit"><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Remarks</Label>
                    <Input value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} data-testid="line-remarks" />
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm" onClick={handleSubmit} data-testid="add-line-submit">
                    <Plus size={14} className="mr-1" />Add Item
                </Button>
            </div>
        </div>
    );
}
